import { customerService } from '@/services/CustomerService';
import { FetchResult } from 'apollo-link';
import { ApolloQueryResult } from 'apollo-client';
import {
  CustomerFields,
  PaginationFields,
  ShipperInput,
  fetchShippers,
  fetchShippersVariables,
  updateShipper
} from '@/types/graphql/schemaTypes';
import { CustomerState, RootState } from '@/types/store';
import { GetterTree, MutationTree, ActionTree, Module } from 'vuex';

// initial state
const state: CustomerState = {
  customer: null,
  customers: [],
  customerOrganizations: [],
  totalCount: 0,
  upsertCustomer: null,
  refresh: false
};

const getters: GetterTree<CustomerState, RootState> = {
  customers(customerState): CustomerFields[] {
    return [...customerState.customers];
  },
  totalCount(customerState): number {
    return customerState.totalCount;
  },
  customer(customerState): CustomerFields | {} {
    return { ...customerState.customer } || {};
  },
  refresh(customerState): boolean {
    return customerState.refresh;
  }
};

// actions
const actions: ActionTree<CustomerState, RootState> = {
  async fetchCustomer({ commit }, customerId: string): Promise<void> {
    customerService.listCustomers({ shipperId: customerId })
      .then((response: ApolloQueryResult<fetchShippers>): void => {
        commit('setCustomer', response.data.shippers[0]);
      });
  },
  async fetchCustomers({ commit }, args: fetchShippersVariables): Promise<void> {
    customerService.listCustomers(args.shipperArgs, args.pagination)
      .then((response: ApolloQueryResult<fetchShippers>): void => {
        commit('setCustomers', response.data.shippers);
        commit('setPagination', response.data.shippersPagination);
      });
  },
  async updateCustomer({ commit }, customerArgs: ShipperInput): Promise<void> {
    customerService.updateCustomer(customerArgs)
      .then((response: FetchResult<updateShipper>): void => {
        const data = response.data as updateShipper;
        commit('setCustomer', data.updateShipper);
      });
  },
  async setUpsertCustomer({ commit }, customer: ShipperInput): Promise<void> {
    commit('setUpsertCustomer', customer);
  },
  async clearUpsert({ commit }): Promise<void> {
    commit('clearUpsert');
  },
  async setRefresh({ commit }): Promise<void> {
    commit('setRefresh');
  }
};

// mutations
const mutations: MutationTree<CustomerState> = {
  setCustomer(customerState, customer: CustomerFields): void {
    customerState.customer = customer;
  },
  setCustomers(customerState, customers: CustomerFields[]): void {
    customerState.customers = customers;
  },
  setPagination(customerState, pagination: PaginationFields): void {
    customerState.totalCount = pagination.totalCount;
  },
  setUpsertCustomer(customerState, upsertCustomer: ShipperInput): void {
    customerState.upsertCustomer = { ...upsertCustomer };
  },
  clearUpsert(customerState): void {
    customerState.upsertCustomer = null;
  },
  setRefresh(customerState, refresh: boolean) {
    customerState.refresh = refresh;
  }
};

export const customer: Module<CustomerState, RootState> = {
  state,
  getters,
  actions,
  mutations,
  namespaced: true
};
